import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { WebsocketService } from 'src/app/config/websocket.service';
import { WebsocketConfigService } from 'src/app/config/websocket-config.service';
import {
  HYGIENE_BUILDING_IMAGE_LIST_TOPIC,
  HYGIENE_BUILDING_LOCATIONS_TOPIC,
  HYGIENE_SUMMARY_TOPIC,
  HYGIENE_UPDATE_DESTINATION,
  HYGIENE_ZONE_INCHARGE_DETAILS_TOPIC,
  HYGIENE_ZONE_INCHARGE_UPDATE_DESTINATION,
} from 'src/app/config/websocket.config';
import { HygieneBuildingLocation, HygineLiveService } from '../hygine-live.service';
import {
  BuildingImagesDTO,
  HygieneSummaryData,
  LiveBuildingUpload,
  ZoneInchargeDetails,
} from './housekeeping-dashboard.models';
import { environment } from 'src/environments/environment';

interface HygieneMapMarker {
  id: string;
  buildingId?: number;
  photoTaken: boolean;
  position: google.maps.LatLngLiteral;
  title: string;
  options: google.maps.MarkerOptions;
}

interface InchargeCard {
  n: string;
  i: number;
  done: number;
  total: number;
  pct: number;
  low: boolean;
}

type FollowUpView = 'city' | 'state' | 'incharge';

const NEWS = [
  'New cleaning SOP v3.2 rolled out to all regions',
  'Monsoon protocol active — wet-floor signage mandatory in 6 states',
  'Quarterly deep-clean audit begins Monday',
  'Supply restock confirmed for West & South zones',
  'Photo-proof upload now required before shift sign-off',
];

/** Default map view — full India */
const INDIA_MAP_CENTER: google.maps.LatLngLiteral = { lat: 20.5937, lng: 78.9629 };
const INDIA_MAP_ZOOM = 5;
const BUILDING_FOCUS_ZOOM = 20;
const MAP_3D_TILT = 60;
const MAP_3D_HEADING = 45;
const MAP_3D_ALTITUDE = 500;
const MAP_3D_BUILDING_RANGE = 1000;
const MAP_3D_INDIA_RANGE = 2600000;
const MAP_3D_DRONE_TILT = 78;
const MAP_3D_DRONE_HEADING = 0;
const MAP_3D_DRONE_ALTITUDE = 1200;
const MAP_3D_DRONE_BUILDING_RANGE = 750;
const MAP_3D_DRONE_INDIA_RANGE = 2200000;
const MAP_3D_DRONE_ORBIT_MS = 90000;
const MAP_3D_MARKER_MODE = 'CLAMP_TO_GROUND';
const GOOGLE_MAP_ID = '9694c58f0185651092852156';
const MAP_FOCUS_REVERT_MS = 20000;
const RIPPLE_FOCUS_DELAY_MS = 400;
const RIPPLE_START_PX = 8;
const RIPPLE_SIZE_SPAN_PX = 42;
const MAP_FOCUS_LABEL_OFFSET_Y = 14;
const INCHARGE_AUTO_SCROLL_MS = 2000;

/** Pentatonic blip scale — matches reference HTML */
const PENTA = [523.25, 587.33, 659.25, 783.99, 880.0, 987.77];

interface DomMapRipple {
  id: string;
  lat: number;
  lng: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
}

interface DomMapFocusLabel {
  name: string;
  lat: number;
  lng: number;
  x: number;
  y: number;
}

interface HkdbMap3DElement extends HTMLElement {
  center?: { lat: number; lng: number; altitude?: number };
  tilt?: number;
  heading?: number;
  range?: number;
  mapId?: string;
  flyCameraTo?: (options: {
    endCamera: {
      center: { lat: number; lng: number; altitude?: number };
      tilt?: number;
      heading?: number;
      range?: number;
    };
    durationMillis?: number;
  }) => void;
  flyCameraAround?: (options: {
    camera: {
      center: { lat: number; lng: number; altitude?: number };
      tilt?: number;
      heading?: number;
      range?: number;
    };
    durationMillis?: number;
    repeatCount?: number;
  }) => void;
  stopCameraAnimation?: () => void;
}

interface HkdbMarker3DElement extends HTMLElement {
  position?: { lat: number; lng: number; altitude?: number };
  altitudeMode?: string;
}

@Component({
  selector: 'app-housekeeping-dashboard',
  templateUrl: './housekeeping-dashboard.component.html',
  styleUrls: ['./housekeeping-dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.hkdb-tv-active]': 'tvView',
  },
})
export class HousekeepingDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(GoogleMap) googleMap?: GoogleMap;
  @ViewChild('map3dHost') map3dHost?: ElementRef<HTMLDivElement>;
  @ViewChild('inchargeGrid') inchargeGrid?: ElementRef<HTMLDivElement>;

  theme: 'dark' | 'light' = 'dark';
  baseMode: 'map' | 'sat' = 'sat';
  mapView3d = false;
  mapViewDrone = false;
  tvView = false;
  soundOn = true;
  selectedIncharge: number | null = null;
  filterInchargeName = '';
  followUpView: FollowUpView = 'incharge';
  inchargeCards: InchargeCard[] = [];
  progressPct = 0;
  expectedPct = 0;
  expectedLabel = 'expected 0%';
  paceStateLabel = 'On track';
  paceStateBg = '';
  paceStateColor = '';
  dateLabel = '—';
  clockLabel = '--:--:--';
  locations: HygieneBuildingLocation[] = [];
  hygieneSummary: HygieneSummaryData | null = null;
  mapCenter: google.maps.LatLngLiteral = { ...INDIA_MAP_CENTER };
  mapZoom = INDIA_MAP_ZOOM;
  mapMarkers: HygieneMapMarker[] = [];
  mapDomRipples: DomMapRipple[] = [];
  mapFocusLabel: DomMapFocusLabel | null = null;
  map3dRippleActive = false;
  mapOptions: google.maps.MapOptions = {};
  staffPendingLabel = 'Staff Pending';
  staffPendingSub = 'not yet logged in';
  staffPendingDisplay = '0';
  liveUpload: LiveBuildingUpload | null = null;
  liveImageIndex = 0;
  liveSliderPaused = false;

  private readonly s3ImageBase = String((environment as any).s3imageUrl || '').trim();
  private readonly liveImageRotateMs = 4500;
  private readonly maxLiveUploadQueue = 30;
  private liveUploadQueue: LiveBuildingUpload[] = [];
  private liveSliderTimer: ReturnType<typeof setInterval> | null = null;
  private liveBrokenImages = new Set<string>();
  private initialStaffImagesApplied = false;
  /** IST calendar day key (YYYY-MM-DD) used to detect midnight rollover. */
  private lastIstDateKey = '';

  private total = 0;
  private totalEmp = 0;
  private doneCount = 0;
  private empIn = 0;
  private hourStamps: number[] = [];
  private reportTimes: number[] = [];
  private statesSeen = new Set<string>();
  private zoneInchargeRows: ZoneInchargeDetails[] = [];
  private cleanSeries: number[] = [];
  private pendSeries: number[] = [];
  private inSeries: number[] = [];
  private lastMilestone = 0;
  private liveTimer: ReturnType<typeof setTimeout> | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private paceTimer: ReturnType<typeof setInterval> | null = null;
  private reportLoopTimer: ReturnType<typeof setTimeout> | null = null;
  private audioCtx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private wsOnConnectUnsubscribe: (() => void) | null = null;
  private wsSummaryUnsubscribe: (() => void) | null = null;
  private wsBuildingImagesUnsubscribe: (() => void) | null = null;
  private wsBuildingLocationUnsubscribe: (() => void) | null = null;
  private wsZoneInchargeUnsubscribe: (() => void) | null = null;
  private readonly rippleIntervals = new Map<string, ReturnType<typeof setInterval>>();
  private readonly reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  private mapFocusRevertTimer: ReturnType<typeof setTimeout> | null = null;
  private mapProjectionOverlay: google.maps.OverlayView | null = null;
  private mapBoundsListener: google.maps.MapsEventListener | null = null;
  private mapZoomListener: google.maps.MapsEventListener | null = null;
  private mapTilesListener: google.maps.MapsEventListener | null = null;
  /** Track which Map instance camera listeners are attached to (survives 3D↔2D recreate). */
  private mapCameraListenerMap: google.maps.Map | null = null;
  private map3dElement: HkdbMap3DElement | null = null;
  private droneOrbitActive = false;
  private readonly map3dMarkerElements = new Map<string, HkdbMarker3DElement>();
  private readonly map3dMarkerMeta = new Map<
    string,
    { photoTaken: boolean; lat: number; lng: number; altitudeMode: string }
  >();
  private inchargeScrollTimer: ReturnType<typeof setInterval> | null = null;
  private inchargeAutoScrollPaused = false;
  private locationsSub: Subscription | null = null;
  private staffImagesSub: Subscription | null = null;
  private readonly resizeHandler = () => this.refreshMapSize();
  private readonly fullscreenHandler = () => this.onFullscreenChange();
  private readonly tvBodyClass = 'hkdb-tv-body-lock';

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly hygineLiveService: HygineLiveService,
    private readonly websocket: WebsocketService,
    private readonly wsConfig: WebsocketConfigService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const routeTv = this.route.snapshot.data['hkdbTvView'] === true;
    const queryTv = this.route.snapshot.queryParamMap.get('tv') === '1';
    if (routeTv || queryTv) {
      this.setTvView(true, { skipFullscreen: false });
    }
    this.loadInitialLocations();
    this.loadInitialStaffImages();
    this.connectWebSocketOnLoad();
    this.syncLayoutSidebarTheme();
  }

  private loadInitialLocations(): void {
    this.locationsSub?.unsubscribe();
    this.locationsSub = this.hygineLiveService.getBuildingLocations().subscribe({
      next: (data: HygieneBuildingLocation[]) => this.setLocations(data),
      error: (err: unknown) => console.error('[HousekeepingDashboard] buildingLocations', err),
    });
  }

  /** Seed Live Upload from getStaffImages for today's IST date (YYYY-MM-DD for LocalDate.parse). */
  private loadInitialStaffImages(): void {
    const today = this.istDateKey();
    this.staffImagesSub?.unsubscribe();
    this.staffImagesSub = this.hygineLiveService.getStaffImages(today).subscribe({
      next: (raw) => {
        const items = this.unwrapStaffImagesResponse(raw);
        console.log('[HousekeepingDashboard] Staff images (initial API):', items);
        // Apply once on page load only
        if (this.initialStaffImagesApplied) {
          return;
        }
        this.initialStaffImagesApplied = true;
        for (const item of items) {
          const upload = this.normalizeBuildingImagesDto(item);
          if (upload.images.length) {
            this.enqueueLiveUpload(upload);
          }
        }
        this.cdr.markForCheck();
      },
      error: (err: unknown) =>
        console.error('[HousekeepingDashboard] getStaffImages', err),
    });
  }

  private istDateKey(d: Date = this.istNow()): string {
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
  }

  private unwrapStaffImagesResponse(raw: unknown): BuildingImagesDTO[] {
    if (Array.isArray(raw)) {
      return raw as BuildingImagesDTO[];
    }
    if (raw && typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      for (const key of ['data', 'result', 'response', 'images', 'imageList']) {
        if (Array.isArray(obj[key])) {
          return obj[key] as BuildingImagesDTO[];
        }
      }
      return [raw as BuildingImagesDTO];
    }
    return [];
  }

  private setLocations(locations: HygieneBuildingLocation[]): void {
    this.locations = locations;
    this.applyBuildingLocationsToMap(locations);
    this.updateMapLegendFromLocations();
    console.log('[HousekeepingDashboard] Building locations (API):', this.locations);

    if (!this.hygieneSummary && locations.length) {
      const photoTaken = locations.filter((loc) => this.isHygienePhotoTaken(loc)).length;
      this.doneCount = photoTaken;
      if (this.total === 0) {
        this.total = locations.length;
      }
      this.rebuildSparkSeries();
      this.refresh(false);
      this.lastMilestone = this.total > 0 ? Math.floor((this.doneCount / this.total) * 20) * 5 : 0;
    }
  }

  private applyBuildingLocationsToMap(locations: HygieneBuildingLocation[]): void {
    const markerByKey = new Map<string, HygieneMapMarker>();

    for (const loc of locations) {
      const marker = this.createMapMarker(loc);
      if (!marker) {
        continue;
      }
      const existing = markerByKey.get(marker.id);
      if (existing) {
        if (marker.photoTaken) {
          markerByKey.set(marker.id, marker);
        }
        continue;
      }
      markerByKey.set(marker.id, marker);
    }

    this.mapMarkers = Array.from(markerByKey.values());
    if (this.showMap3dElement) {
      void this.ensureMap3dElement().then(() => this.syncMap3dMarkers());
    }
    this.focusMapOnIndia();
  }

  private createMapMarker(loc: HygieneBuildingLocation): HygieneMapMarker | null {
    const lat = parseFloat(String(loc.latitude ?? ''));
    const lng = parseFloat(String(loc.longitude ?? ''));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    const photoTaken = this.isHygienePhotoTaken(loc);
    const title = loc.buildingName || 'Building';
    const id = this.markerKey(loc, lat, lng);

    return {
      id,
      buildingId: loc.buildingId,
      photoTaken,
      position: { lat, lng },
      title,
      options: this.buildHygieneMapMarkerOptions(photoTaken, title),
    };
  }

  private markerKey(loc: HygieneBuildingLocation, lat?: number, lng?: number): string {
    const la = lat ?? parseFloat(String(loc.latitude ?? ''));
    const ln = lng ?? parseFloat(String(loc.longitude ?? ''));
    return `${loc.buildingId ?? ''}-${la.toFixed(6)},${ln.toFixed(6)}`;
  }

  private findMarkerIndex(loc: HygieneBuildingLocation): number {
    if (loc.buildingId != null) {
      const byId = this.mapMarkers.findIndex((marker) => marker.buildingId === loc.buildingId);
      if (byId >= 0) {
        return byId;
      }
    }

    const lat = parseFloat(String(loc.latitude ?? ''));
    const lng = parseFloat(String(loc.longitude ?? ''));
    const key = this.markerKey(loc, lat, lng);
    return this.mapMarkers.findIndex((marker) => marker.id === key);
  }

  private upsertLocationRecord(loc: HygieneBuildingLocation): {
    wasPhotoTaken: boolean;
    isPhotoTaken: boolean;
  } {
    const idx = this.locations.findIndex((item) => item.buildingId === loc.buildingId);
    const wasPhotoTaken = idx >= 0 ? this.isHygienePhotoTaken(this.locations[idx]) : false;
    const isPhotoTaken = this.isHygienePhotoTaken(loc);

    if (idx >= 0) {
      this.locations[idx] = { ...this.locations[idx], ...loc };
    } else {
      this.locations.push(loc);
    }

    return { wasPhotoTaken, isPhotoTaken };
  }

  private upsertMapMarker(loc: HygieneBuildingLocation, showRipple: boolean): void {
    const marker = this.createMapMarker(loc);
    if (!marker) {
      return;
    }

    const markerIdx = this.findMarkerIndex(loc);
    if (markerIdx >= 0) {
      this.mapMarkers[markerIdx] = marker;
    } else {
      this.mapMarkers.push(marker);
    }

    this.mapMarkers = [...this.mapMarkers];

    if (this.showMap3dElement) {
      void this.syncMap3dMarkers();
    }

    if (showRipple) {
      this.focusMapOnBuilding(loc);
      setTimeout(() => this.playMapRipple(marker.position), RIPPLE_FOCUS_DELAY_MS);
    }
  }

  private ensureMapProjection(): google.maps.Map | null {
    const map = this.googleMap?.googleMap ?? null;
    if (!map) {
      return null;
    }

    // google-map is destroyed/recreated when toggling 3D; drop stale overlay/listeners.
    const overlayMap = this.mapProjectionOverlay?.getMap?.() ?? null;
    if (this.mapProjectionOverlay && overlayMap !== map) {
      this.resetGoogleMapProjectionState();
    }

    if (!this.mapProjectionOverlay) {
      const overlay = new google.maps.OverlayView();
      overlay.onAdd = () => {};
      overlay.draw = () => {};
      overlay.setMap(map);
      this.mapProjectionOverlay = overlay;

      this.mapBoundsListener = map.addListener('bounds_changed', () => {
        if (this.mapDomRipples.length) {
          this.syncDomRipplePositions();
        }
        if (this.mapFocusLabel) {
          this.syncMapFocusLabelPosition();
        }
      });
    }

    return map;
  }

  /** Clears 2D projection helpers so they can reattach after the map is recreated. */
  private resetGoogleMapProjectionState(): void {
    this.mapBoundsListener?.remove();
    this.mapBoundsListener = null;
    this.mapZoomListener?.remove();
    this.mapZoomListener = null;
    this.mapTilesListener?.remove();
    this.mapTilesListener = null;
    this.mapCameraListenerMap = null;
    this.mapProjectionOverlay?.setMap(null);
    this.mapProjectionOverlay = null;
  }

  private projectLatLngToContainer(lat: number, lng: number): { x: number; y: number } | null {
    const projection = this.mapProjectionOverlay?.getProjection();
    if (!projection) {
      return null;
    }

    const point = projection.fromLatLngToContainerPixel(new google.maps.LatLng(lat, lng));
    if (!point) {
      return null;
    }

    return { x: point.x, y: point.y };
  }

  private syncDomRipplePositions(): void {
    if (!this.mapDomRipples.length) {
      return;
    }

    let changed = false;
    const next = this.mapDomRipples.map((ripple) => {
      const point = this.projectLatLngToContainer(ripple.lat, ripple.lng);
      if (!point || (point.x === ripple.x && point.y === ripple.y)) {
        return ripple;
      }
      changed = true;
      return { ...ripple, x: point.x, y: point.y };
    });

    if (changed) {
      this.mapDomRipples = next;
      this.cdr.markForCheck();
    }
  }

  private syncMapFocusLabelPosition(): void {
    if (!this.mapFocusLabel) {
      return;
    }

    const point = this.projectLatLngToContainer(this.mapFocusLabel.lat, this.mapFocusLabel.lng);
    if (!point) {
      return;
    }

    const y = point.y + MAP_FOCUS_LABEL_OFFSET_Y;
    if (point.x === this.mapFocusLabel.x && y === this.mapFocusLabel.y) {
      return;
    }

    this.mapFocusLabel = {
      ...this.mapFocusLabel,
      x: point.x,
      y,
    };
    this.cdr.markForCheck();
  }

  private showMapFocusLabel(
    position: google.maps.LatLngLiteral,
    name: string,
    attempt = 0,
  ): void {
    if (!this.ensureMapProjection()) {
      if (attempt < 8) {
        setTimeout(() => this.showMapFocusLabel(position, name, attempt + 1), 80);
      }
      return;
    }

    const point = this.projectLatLngToContainer(position.lat, position.lng);
    if (!point) {
      if (attempt < 8) {
        setTimeout(() => this.showMapFocusLabel(position, name, attempt + 1), 80);
      }
      return;
    }

    this.ngZone.run(() => {
      this.mapFocusLabel = {
        name,
        lat: position.lat,
        lng: position.lng,
        x: point.x,
        y: point.y + MAP_FOCUS_LABEL_OFFSET_Y,
      };
      this.cdr.markForCheck();
    });
  }

  private clearMapFocusLabel(): void {
    if (!this.mapFocusLabel) {
      return;
    }

    this.mapFocusLabel = null;
    this.cdr.markForCheck();
  }

  /** Expanding green ring — pixel overlay matches reference HTML ripple() */
  private playMapRipple(center: google.maps.LatLngLiteral, attempt = 0): void {
    if (this.reduceMotion) {
      return;
    }

    if (!this.ensureMapProjection()) {
      if (attempt < 8) {
        setTimeout(() => this.playMapRipple(center, attempt + 1), 80);
      }
      return;
    }

    const point = this.projectLatLngToContainer(center.lat, center.lng);
    if (!point) {
      if (attempt < 8) {
        setTimeout(() => this.playMapRipple(center, attempt + 1), 80);
      }
      return;
    }

    const id = `rip-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const ripple: DomMapRipple = {
      id,
      lat: center.lat,
      lng: center.lng,
      x: point.x,
      y: point.y,
      size: RIPPLE_START_PX,
      opacity: 0.95,
    };

    this.ngZone.run(() => {
      this.mapDomRipples = [...this.mapDomRipples, ripple];
      this.cdr.markForCheck();
    });

    let k = 0;
    const intervalId = setInterval(() => {
      k += 0.07;
      const idx = this.mapDomRipples.findIndex((item) => item.id === id);
      if (idx < 0) {
        clearInterval(intervalId);
        this.rippleIntervals.delete(id);
        return;
      }

      const pointNow = this.projectLatLngToContainer(center.lat, center.lng);
      this.ngZone.run(() => {
        const current = this.mapDomRipples[idx];
        this.mapDomRipples[idx] = {
          ...current,
          x: pointNow?.x ?? current.x,
          y: pointNow?.y ?? current.y,
          size: RIPPLE_START_PX + k * RIPPLE_SIZE_SPAN_PX,
          opacity: Math.max(0, 0.95 * (1 - k)),
        };
        this.mapDomRipples = [...this.mapDomRipples];
        this.cdr.markForCheck();
      });

      if (k >= 1) {
        clearInterval(intervalId);
        this.rippleIntervals.delete(id);
        this.ngZone.run(() => {
          this.mapDomRipples = this.mapDomRipples.filter((item) => item.id !== id);
          this.cdr.markForCheck();
        });
      }
    }, 16);

    this.rippleIntervals.set(id, intervalId);
  }

  trackMapRipple(_index: number, ripple: DomMapRipple): string {
    return ripple.id;
  }

  trackMapMarker(_index: number, marker: HygieneMapMarker): string {
    return marker.id;
  }

  onMapMarkerClick(marker: HygieneMapMarker): void {
    this.ngZone.run(() => {
      this.focusMapOnPosition(marker.position, {
        revert: true,
        ripple: true,
        buildingName: marker.title,
      });
    });
  }

  private isHygienePhotoTaken(loc: HygieneBuildingLocation): boolean {
    const status = loc.hygienePhotoStatus;
    if (status === 1 || status === '1') {
      return true;
    }
    if (status === 0 || status === '0') {
      return false;
    }
    return !!loc.hygienePhotoTaken;
  }

  private buildHygieneMapMarkerOptions(
    photoTaken: boolean,
    title: string,
  ): google.maps.MarkerOptions {
    const fill = photoTaken ? '#22c55e' : '#ef4444';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="5" fill="${fill}" stroke="#ffffff" stroke-width="1.5"/></svg>`;
    return {
      title,
      clickable: true,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: { width: 12, height: 12 },
        anchor: { x: 6, y: 6 },
      } as google.maps.Icon,
    };
  }

  private focusMapOnIndia(): void {
    this.clearMapFocusLabel();
    this.mapCenter = { ...INDIA_MAP_CENTER };
    this.mapZoom = INDIA_MAP_ZOOM;
    if (this.showMap3dElement) {
      void this.ensureMap3dElement().then(() => {
        this.applyMap3dCamera(INDIA_MAP_CENTER, this.resolveMap3dRange(false), true);
        this.syncDroneOrbit(true);
      });
      return;
    }

    const map = this.googleMap?.googleMap;
    if (map) {
      map.panTo(INDIA_MAP_CENTER);
      map.setZoom(INDIA_MAP_ZOOM);
      this.applyMapCamera();
    }
  }

  private focusMapOnBuilding(loc: HygieneBuildingLocation): void {
    const lat = parseFloat(String(loc.latitude ?? ''));
    const lng = parseFloat(String(loc.longitude ?? ''));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    this.ngZone.run(() => {
      this.focusMapOnPosition(
        { lat, lng },
        { revert: true, ripple: true, buildingName: loc.buildingName || 'Building' },
      );
    });
  }

  private focusMapOnPosition(
    position: google.maps.LatLngLiteral,
    options: { revert?: boolean; ripple?: boolean; buildingName?: string } = {},
  ): void {
    const { revert = false, ripple = true, buildingName } = options;

    if (this.mapFocusRevertTimer) {
      clearTimeout(this.mapFocusRevertTimer);
      this.mapFocusRevertTimer = null;
    }

    this.mapCenter = { ...position };
    this.mapZoom = BUILDING_FOCUS_ZOOM;

    if (this.showMap3dElement) {
      void this.ensureMap3dElement().then(() => {
        this.stopDroneOrbit();
        this.applyMap3dCamera(position, this.resolveMap3dRange(true), true);
        if (revert) {
          this.syncDroneOrbit(false);
        }
      });
    } else {
      const map = this.googleMap?.googleMap;
      if (map) {
        map.panTo(position);
        map.setZoom(BUILDING_FOCUS_ZOOM);
        this.applyMapCamera();
      }
    }

    if (ripple) {
      if (this.showMap3dElement) {
        setTimeout(() => this.triggerMap3dFocusRipple(), RIPPLE_FOCUS_DELAY_MS);
      } else {
        setTimeout(() => this.playMapRipple(position), RIPPLE_FOCUS_DELAY_MS);
      }
    }

    if (buildingName) {
      if (this.showMap3dElement) {
        setTimeout(() => {
          this.mapFocusLabel = {
            name: buildingName,
            lat: position.lat,
            lng: position.lng,
            x: 0,
            y: 0,
          };
          this.cdr.markForCheck();
        }, RIPPLE_FOCUS_DELAY_MS);
      } else {
        setTimeout(() => this.showMapFocusLabel(position, buildingName), RIPPLE_FOCUS_DELAY_MS);
      }
    } else {
      this.clearMapFocusLabel();
    }

    if (revert) {
      this.scheduleMapFocusRevert();
    }

    this.cdr.markForCheck();
  }

  private scheduleMapFocusRevert(): void {
    if (this.mapFocusRevertTimer) {
      clearTimeout(this.mapFocusRevertTimer);
    }

    this.mapFocusRevertTimer = setTimeout(() => {
      this.mapFocusRevertTimer = null;
      this.focusMapOnIndia();
    }, MAP_FOCUS_REVERT_MS);
  }

  private updateMapLegendFromLocations(anim = false): void {
    const photoTaken = this.locations.filter((loc) => this.isHygienePhotoTaken(loc)).length;
    const pending = Math.max(0, this.locations.length - photoTaken);

    if (anim) {
      const doneEl = this.$('mapDone');
      const pendEl = this.$('mapPend');
      if (doneEl) {
        this.animateNum(doneEl, photoTaken);
      }
      if (pendEl) {
        this.animateNum(pendEl, pending);
      }
      return;
    }

    this.setText('mapDone', this.fmt(photoTaken));
    this.setText('mapPend', this.fmt(pending));
  }

  private initGoogleMap(): void {
    this.updateGoogleMapOptions();
    this.clearMapFocusLabel();
    this.mapCenter = { ...INDIA_MAP_CENTER };
    this.mapZoom = INDIA_MAP_ZOOM;
    this.cdr.detectChanges();
    void this.syncMap3dMode();
  }

  get showMap3dElement(): boolean {
    return this.mapView3d && this.baseMode === 'sat';
  }

  private async syncMap3dMode(): Promise<void> {
    if (!this.showMap3dElement) {
      this.destroyMap3dElement();
      // 2D google-map was removed while in 3D; clear stale projection and rebind.
      this.resetGoogleMapProjectionState();
      this.cdr.detectChanges();
      await this.rebindGoogleMapAfterModeSwitch();
      return;
    }

    // Leaving 2D — map instance will be destroyed by *ngIf.
    this.resetGoogleMapProjectionState();

    try {
      await this.ensureMap3dElement();
      await this.syncMap3dMarkers();
      const camera = this.resolveMap3dCamera();
      this.applyMap3dCamera(camera.position, camera.range);
      this.syncDroneOrbit(this.isIndiaMapView());
    } catch (err) {
      console.error('[HousekeepingDashboard] Map3D init failed', err);
      this.mapView3d = false;
      this.resetGoogleMapProjectionState();
      this.cdr.detectChanges();
      await this.rebindGoogleMapAfterModeSwitch();
    }
  }

  /** Wait for the recreated 2D map, then restore camera listeners and building labels. */
  private async rebindGoogleMapAfterModeSwitch(attempt = 0): Promise<void> {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    const map = this.googleMap?.googleMap;
    if (!map) {
      if (attempt < 24) {
        await new Promise<void>((resolve) => setTimeout(resolve, 50));
        return this.rebindGoogleMapAfterModeSwitch(attempt + 1);
      }
      return;
    }

    this.applyMapCamera();
    this.attachMapCameraListeners();
    this.ensureMapProjection();

    // 3D labels are CSS-centered; reproject to pixel coords for satellite/2D.
    if (this.mapFocusLabel) {
      const { lat, lng, name } = this.mapFocusLabel;
      this.showMapFocusLabel({ lat, lng }, name);
    }
    if (this.mapDomRipples.length) {
      this.syncDomRipplePositions();
    }

    this.cdr.markForCheck();
  }

  private resolveMap3dCamera(): { position: google.maps.LatLngLiteral; range: number } {
    if (this.mapZoom >= BUILDING_FOCUS_ZOOM - 2) {
      return { position: this.mapCenter, range: this.resolveMap3dRange(true) };
    }
    return { position: { ...INDIA_MAP_CENTER }, range: this.resolveMap3dRange(false) };
  }

  private resolveMap3dRange(buildingFocus: boolean): number {
    if (buildingFocus) {
      return this.mapViewDrone ? MAP_3D_DRONE_BUILDING_RANGE : MAP_3D_BUILDING_RANGE;
    }
    return this.mapViewDrone ? MAP_3D_DRONE_INDIA_RANGE : MAP_3D_INDIA_RANGE;
  }

  private getMap3dCameraAngles(): { tilt: number; heading: number; altitude: number } {
    if (this.mapViewDrone) {
      return {
        tilt: MAP_3D_DRONE_TILT,
        heading: MAP_3D_DRONE_HEADING,
        altitude: MAP_3D_DRONE_ALTITUDE,
      };
    }
    return {
      tilt: MAP_3D_TILT,
      heading: MAP_3D_HEADING,
      altitude: MAP_3D_ALTITUDE,
    };
  }

  private isIndiaMapView(): boolean {
    return this.mapZoom < BUILDING_FOCUS_ZOOM - 2;
  }

  private stopDroneOrbit(): void {
    if (this.droneOrbitActive) {
      this.map3dElement?.stopCameraAnimation?.();
      this.droneOrbitActive = false;
    }
  }

  private startDroneOrbit(position: google.maps.LatLngLiteral = INDIA_MAP_CENTER): void {
    if (!this.mapViewDrone || !this.showMap3dElement || !this.map3dElement?.flyCameraAround) {
      return;
    }

    this.map3dElement.stopCameraAnimation?.();
    this.droneOrbitActive = false;

    const angles = this.getMap3dCameraAngles();
    this.map3dElement.flyCameraAround({
      camera: {
        center: {
          lat: position.lat,
          lng: position.lng,
          altitude: angles.altitude,
        },
        tilt: angles.tilt,
        heading: angles.heading,
        range: MAP_3D_DRONE_INDIA_RANGE,
      },
      durationMillis: MAP_3D_DRONE_ORBIT_MS,
      repeatCount: Number.POSITIVE_INFINITY,
    });
    this.droneOrbitActive = true;
  }

  private syncDroneOrbit(atIndiaView: boolean): void {
    if (!this.mapViewDrone || !this.showMap3dElement) {
      this.stopDroneOrbit();
      return;
    }

    if (atIndiaView) {
      this.startDroneOrbit();
      return;
    }

    this.stopDroneOrbit();
  }

  private async ensureMap3dElement(attempt = 0): Promise<void> {
    if (this.map3dElement) {
      return;
    }

    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    const host = this.map3dHost?.nativeElement;
    if (!host) {
      if (attempt < 12) {
        return this.ensureMap3dElement(attempt + 1);
      }
      throw new Error('3D map host element is not available');
    }

    const { Map3DElement } = (await google.maps.importLibrary('maps3d')) as {
      Map3DElement: new (options: Record<string, unknown>) => HkdbMap3DElement;
    };

    const angles = this.getMap3dCameraAngles();
    const map = new Map3DElement({
      mode: 'HYBRID',
      center: {
        lat: INDIA_MAP_CENTER.lat,
        lng: INDIA_MAP_CENTER.lng,
        altitude: angles.altitude,
      },
      tilt: angles.tilt,
      heading: angles.heading,
      range: this.resolveMap3dRange(false),
      mapId: GOOGLE_MAP_ID,
    });

    map.style.width = '100%';
    map.style.height = '100%';
    map.style.display = 'block';

    map.addEventListener('gmp-map-id-error', () => {
      console.error('[HousekeepingDashboard] Invalid Map3D mapId:', GOOGLE_MAP_ID);
    });

    host.append(map);
    this.map3dElement = map;
  }

  private async syncMap3dMarkers(): Promise<void> {
    if (!this.map3dElement) {
      return;
    }

    const [maps3dLib, markerLib] = await Promise.all([
      google.maps.importLibrary('maps3d'),
      google.maps.importLibrary('marker'),
    ]);
    const { Marker3DElement } = maps3dLib as unknown as {
      Marker3DElement: new (options: Record<string, unknown>) => HkdbMarker3DElement;
    };
    const { PinElement } = markerLib as unknown as {
      PinElement: new (options: Record<string, unknown>) => HTMLElement;
    };

    const nextIds = new Set(this.mapMarkers.map((marker) => marker.id));

    for (const [id, element] of [...this.map3dMarkerElements]) {
      if (!nextIds.has(id)) {
        this.removeMap3dMarker(id, element);
      }
    }

    for (const marker of this.mapMarkers) {
      const meta = this.map3dMarkerMeta.get(marker.id);
      const unchanged =
        meta &&
        meta.photoTaken === marker.photoTaken &&
        meta.lat === marker.position.lat &&
        meta.lng === marker.position.lng &&
        meta.altitudeMode === MAP_3D_MARKER_MODE;

      if (unchanged && this.map3dMarkerElements.has(marker.id)) {
        continue;
      }

      const existing = this.map3dMarkerElements.get(marker.id);
      if (existing) {
        this.removeMap3dMarker(marker.id, existing);
      }

      const marker3d = new Marker3DElement({
        position: {
          lat: marker.position.lat,
          lng: marker.position.lng,
        },
        altitudeMode: MAP_3D_MARKER_MODE,
      });

      const pin = new PinElement({
        background: marker.photoTaken ? '#22c55e' : '#ef4444',
        borderColor: '#ffffff',
        scale: 0.65,
      });
      marker3d.append(pin);

      marker3d.addEventListener('gmp-click', () => {
        this.ngZone.run(() => this.onMapMarkerClick(marker));
      });

      this.map3dElement.append(marker3d);
      this.map3dMarkerElements.set(marker.id, marker3d);
      this.map3dMarkerMeta.set(marker.id, {
        photoTaken: marker.photoTaken,
        lat: marker.position.lat,
        lng: marker.position.lng,
        altitudeMode: MAP_3D_MARKER_MODE,
      });
    }
  }

  private removeMap3dMarker(id: string, element: HkdbMarker3DElement): void {
    element.remove();
    this.map3dMarkerElements.delete(id);
    this.map3dMarkerMeta.delete(id);
  }

  private clearMap3dMarkers(): void {
    for (const [id, element] of [...this.map3dMarkerElements]) {
      this.removeMap3dMarker(id, element);
    }
  }

  private destroyMap3dElement(): void {
    this.stopDroneOrbit();
    this.clearMap3dMarkers();
    this.map3dElement?.remove();
    this.map3dElement = null;
    this.map3dHost?.nativeElement.replaceChildren();
    this.map3dRippleActive = false;
  }

  private applyMap3dCamera(
    position: google.maps.LatLngLiteral = this.mapCenter,
    range: number = MAP_3D_BUILDING_RANGE,
    animate = false,
  ): void {
    if (!this.map3dElement) {
      return;
    }

    const angles = this.getMap3dCameraAngles();
    const endCamera = {
      center: {
        lat: position.lat,
        lng: position.lng,
        altitude: angles.altitude,
      },
      tilt: angles.tilt,
      heading: angles.heading,
      range,
    };

    if (animate && this.map3dElement.flyCameraTo) {
      this.map3dElement.flyCameraTo({ endCamera, durationMillis: 1400 });
      return;
    }

    this.map3dElement.center = endCamera.center;
    this.map3dElement.tilt = endCamera.tilt;
    this.map3dElement.heading = endCamera.heading;
    this.map3dElement.range = range;
  }

  private triggerMap3dFocusRipple(): void {
    if (this.reduceMotion) {
      return;
    }

    this.map3dRippleActive = false;
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.map3dRippleActive = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.map3dRippleActive = false;
        this.cdr.markForCheck();
      }, 1200);
    });
  }

  private attachMapCameraListeners(): void {
    const map = this.googleMap?.googleMap;
    if (!map) {
      return;
    }

    // Already wired to this map instance.
    if (this.mapCameraListenerMap === map && this.mapZoomListener) {
      return;
    }

    // Recreated map after 3D toggle — drop listeners from the previous instance.
    this.mapZoomListener?.remove();
    this.mapZoomListener = null;
    this.mapTilesListener?.remove();
    this.mapTilesListener = null;

    this.mapCameraListenerMap = map;

    this.mapZoomListener = map.addListener('zoom_changed', () => {
      const zoom = map.getZoom();
      if (zoom != null) {
        this.mapZoom = zoom;
      }
      this.applyMapCamera();
    });

    this.mapTilesListener = map.addListener('tilesloaded', () => {
      this.applyMapCamera();
      if (this.mapFocusLabel) {
        this.syncMapFocusLabelPosition();
      }
      if (this.mapDomRipples.length) {
        this.syncDomRipplePositions();
      }
    });
  }

  private updateGoogleMapOptions(): void {
    this.mapOptions = {
      mapId: GOOGLE_MAP_ID,
      mapTypeId: this.baseMode === 'sat' ? 'hybrid' : 'roadmap',
      disableDefaultUI: true,
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      rotateControl: false,
    };
  }

  private applyMapCamera(): void {
    if (this.showMap3dElement) {
      return;
    }

    const map = this.googleMap?.googleMap;
    if (!map) {
      return;
    }

    const mapTypeId =
      this.baseMode === 'sat' ? google.maps.MapTypeId.HYBRID : google.maps.MapTypeId.ROADMAP;
    map.setMapTypeId(mapTypeId);
    map.setTilt(0);
  }

  onMapIdle(): void {
    this.attachMapCameraListeners();
    if (!this.showMap3dElement) {
      this.ensureMapProjection();
      this.applyMapCamera();
      if (this.mapFocusLabel) {
        this.syncMapFocusLabelPosition();
      }
      if (this.mapDomRipples.length) {
        this.syncDomRipplePositions();
      }
    }
  }

  onMapZoomChanged(): void {
    const map = this.googleMap?.googleMap;
    const zoom = map?.getZoom();
    if (zoom != null) {
      this.mapZoom = zoom;
    }
    if (!this.showMap3dElement) {
      this.applyMapCamera();
      if (this.mapFocusLabel) {
        this.syncMapFocusLabelPosition();
      }
    }
  }

  onMapTilesLoaded(): void {
    if (!this.showMap3dElement) {
      this.ensureMapProjection();
      this.applyMapCamera();
      if (this.mapFocusLabel) {
        this.syncMapFocusLabelPosition();
      }
      if (this.mapDomRipples.length) {
        this.syncDomRipplePositions();
      }
    }
  }

  private refreshMapSize(): void {
    const map = this.googleMap?.googleMap;
    if (map) {
      google.maps.event.trigger(map, 'resize');
      this.applyMapCamera();
    }
  }

  private connectWebSocketOnLoad(): void {
    this.wsConfig.refresh();
    if (!this.wsConfig.isEnabled()) {
      return;
    }

    this.wsSummaryUnsubscribe = this.websocket.subscribe(HYGIENE_SUMMARY_TOPIC, (message) => {
      this.handleHygieneSummaryMessage(message);
    });

    this.wsBuildingImagesUnsubscribe = this.websocket.subscribe(
      HYGIENE_BUILDING_IMAGE_LIST_TOPIC,
      (message) => this.handleBuildingImagesMessage(message),
    );

    this.wsBuildingLocationUnsubscribe = this.websocket.subscribe(
      HYGIENE_BUILDING_LOCATIONS_TOPIC,
      (message) => this.handleBuildingLocationMessage(message),
    );

    this.wsZoneInchargeUnsubscribe = this.websocket.subscribe(
      HYGIENE_ZONE_INCHARGE_DETAILS_TOPIC,
      (message) => this.handleZoneInchargeDetailsMessage(message),
    );

    this.wsOnConnectUnsubscribe = this.websocket.onConnect(() => {
      console.log('[HousekeepingDashboard] WebSocket connected');
      console.log('[HousekeepingDashboard] Requesting hygiene summary via', HYGIENE_UPDATE_DESTINATION);
      console.log(
        '[HousekeepingDashboard] Requesting zone incharge details via',
        HYGIENE_ZONE_INCHARGE_UPDATE_DESTINATION,
      );

      setTimeout(() => {
        this.websocket.publish({ destination: HYGIENE_UPDATE_DESTINATION });
        this.websocket.publish({ destination: HYGIENE_ZONE_INCHARGE_UPDATE_DESTINATION });
      }, 150);
    });

    this.websocket.connect();
  }

  private handleBuildingLocationMessage(message: { body: string }): void {
    try {
      const parsed = JSON.parse(message.body) as HygieneBuildingLocation | HygieneBuildingLocation[] | { data?: unknown };
      const raw = Array.isArray(parsed)
        ? parsed[0]
        : (parsed as { data?: unknown }).data ?? parsed;
      const list = Array.isArray(raw) ? raw : [raw];
      const [loc] = this.hygineLiveService.normalizeBuildingLocations(list);

      if (!loc) {
        return;
      }

      console.log('[HousekeepingDashboard] Building location (WebSocket):', loc);
      this.applyLiveBuildingLocationUpdate(loc);
    } catch (err) {
      console.error('[HousekeepingDashboard] Failed to parse building location', err, message.body);
    }
  }

  private applyLiveBuildingLocationUpdate(loc: HygieneBuildingLocation): void {
    const { wasPhotoTaken, isPhotoTaken } = this.upsertLocationRecord(loc);

    if (wasPhotoTaken && isPhotoTaken) {
      this.upsertMapMarker(loc, true);
      this.blip(loc.buildingId != null ? Number(loc.buildingId) : this.doneCount);
      return;
    }

    const becamePhotoTaken = !wasPhotoTaken && isPhotoTaken;
    this.upsertMapMarker(loc, becamePhotoTaken);

    if (becamePhotoTaken) {
      this.onBuildingPhotoTaken(loc);
      return;
    }

    this.updateMapLegendFromLocations();
  }

  private onBuildingPhotoTaken(loc: HygieneBuildingLocation): void {
    this.blip(loc.buildingId != null ? Number(loc.buildingId) : this.doneCount);

    this.doneCount += 1;
    this.progressPct = this.total > 0 ? Math.round((this.doneCount / this.total) * 100) : 0;
    this.pop('dClean');
    this.rebuildSparkSeries();
    this.updateMapLegendFromLocations(true);
    this.setNumbers(true);
    this.setText('sCleanPct', `${this.progressPct}%`);
    this.setText('sPendPct', `${this.total > 0 ? 100 - this.progressPct : 0}%`);
    this.setText('progPct', String(this.progressPct));
    this.updatePace();
    this.drawAllSparks();
    this.checkMilestone();
  }

  private handleHygieneSummaryMessage(message: { body: string }): void {
    try {
      const summary = JSON.parse(message.body) as HygieneSummaryData;
      console.log('[HousekeepingDashboard] Hygiene summary (WebSocket):', summary);
      this.applyHygieneSummary(summary);
    } catch (err) {
      console.error('[HousekeepingDashboard] Failed to parse hygiene summary', err, message.body);
    }
  }

  private handleBuildingImagesMessage(message: { body: string }): void {
    try {
      const parsed = JSON.parse(message.body) as BuildingImagesDTO | BuildingImagesDTO[];
      const items = Array.isArray(parsed) ? parsed : [parsed];
      console.log('[HousekeepingDashboard] Building images (WebSocket):', items);

      for (const item of items) {
        const upload = this.normalizeBuildingImagesDto(item);
        if (upload.images.length) {
          this.enqueueLiveUpload(upload);
        }
      }
    } catch (err) {
      console.error('[HousekeepingDashboard] Failed to parse building images', err, message.body);
    }
  }

  private handleZoneInchargeDetailsMessage(message: { body: string }): void {
    try {
      const rows = this.unwrapZoneInchargeDetails(JSON.parse(message.body));
      console.log('[HousekeepingDashboard] Zone incharge details (WebSocket):', rows);
      this.zoneInchargeRows = rows;
      this.renderIncharges();
      this.cdr.markForCheck();
    } catch (err) {
      console.error('[HousekeepingDashboard] Failed to parse zone incharge details', err, message.body);
    }
  }

  private unwrapZoneInchargeDetails(raw: unknown): ZoneInchargeDetails[] {
    if (Array.isArray(raw)) {
      return raw as ZoneInchargeDetails[];
    }
    if (raw && typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      for (const key of [
        'data',
        'result',
        'response',
        'zoneinchargeSummary',
        'zoneinchargeDetails',
        'zoneInchargeDetails',
        'inchargeDetails',
        'list',
      ]) {
        if (Array.isArray(obj[key])) {
          return obj[key] as ZoneInchargeDetails[];
        }
      }
      return [raw as ZoneInchargeDetails];
    }
    return [];
  }

  private followUpLabel(row: ZoneInchargeDetails, index: number): string {
    if (this.followUpView === 'city') {
      return this.pickRowText(row, 'cityName', 'city', 'city_name') || 'Unknown city';
    }
    if (this.followUpView === 'state') {
      return this.pickRowText(row, 'stateName', 'state', 'state_name') || 'Unknown state';
    }
    return this.pickRowText(row, 'zoneIncharge', 'incharge', 'inChargeName') || `In-charge ${index + 1}`;
  }

  private pickRowText(row: ZoneInchargeDetails, ...keys: string[]): string {
    for (const key of keys) {
      const value = row[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return '';
  }

  private toNonNegNumber(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
  }

  private toOptionalNumber(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  private normalizeBuildingImagesDto(dto: BuildingImagesDTO): LiveBuildingUpload {
    const images = this.extractImageUrls(dto);
    const staffCount = this.resolveStaffCount(dto);
    const uploadedAt = this.resolveUploadedAt(dto);

    return {
      buildingId: dto.buildingId != null ? Number(dto.buildingId) : undefined,
      buildingCode: String(dto.fusionBuildingCodeProd || '').trim() || undefined,
      buildingName: String(dto.buildingName || dto.campusName || 'Building').trim(),
      campusName: String(dto.campusName || dto.buildingName || '').trim(),
      stateName: String(dto.stateName || dto.state || dto.sessionType || '').trim(),
      staffCount,
      departmentName: String(dto.departmentName || 'Housekeeping').trim(),
      images,
      uploadedBy: String(dto.employeeName || dto.uploadedBy || dto.uploadedByName || 'Staff').trim(),
      uploadedAt,
      receivedAt: Date.now(),
    };
  }

  private resolveStaffCount(dto: BuildingImagesDTO): number {
    if (dto.staffCount != null && Number.isFinite(Number(dto.staffCount))) {
      return Math.max(0, Number(dto.staffCount));
    }
    if (Array.isArray(dto.staffDetails)) {
      return dto.staffDetails.length;
    }
    if (Array.isArray(dto.staffNames)) {
      return dto.staffNames.length;
    }
    return 0;
  }

  private resolveUploadedAt(dto: BuildingImagesDTO): string {
    if (Array.isArray(dto.imageDetails) && dto.imageDetails.length) {
      for (const detail of dto.imageDetails) {
        const time = detail?.uplodedTime || detail?.uploadedTime || detail?.uploadedOn;
        if (time) {
          return String(time);
        }
      }
    }
    return String(dto.uploadedOn || dto.hygieneDate || dto.createdOn || '');
  }

  private extractImageUrls(dto: BuildingImagesDTO): string[] {
    const urls: string[] = [];

    const pushUrl = (raw: unknown) => {
      if (typeof raw !== 'string' || !raw.trim()) {
        return;
      }
      urls.push(this.resolveImageUrl(raw));
    };

    if (Array.isArray(dto.imageDetails)) {
      for (const detail of dto.imageDetails) {
        if (!detail || typeof detail !== 'object') {
          continue;
        }
        pushUrl(detail.url || detail.imageUrl || detail.fileName);
      }
    }

    dto.buildingImageUrlList?.forEach(pushUrl);
    dto.imageUrlList?.forEach(pushUrl);

    if (Array.isArray(dto.images)) {
      for (const entry of dto.images) {
        if (typeof entry === 'string') {
          pushUrl(entry);
        } else if (entry && typeof entry === 'object') {
          pushUrl(entry.url || entry.imageUrl || entry.fileName);
        }
      }
    }

    dto.pictures?.forEach((pic) => pushUrl(pic.imageUrl || pic.fileName));

    return [...new Set(urls)];
  }

  private resolveImageUrl(raw: string): string {
    const value = raw.trim();
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    if (!this.s3ImageBase) {
      return value;
    }
    const path = value.startsWith('/') ? value.slice(1) : value;
    const base = this.s3ImageBase.endsWith('/') ? this.s3ImageBase : `${this.s3ImageBase}/`;
    return `${base}${path}`;
  }

  private enqueueLiveUpload(upload: LiveBuildingUpload): void {
    this.liveUploadQueue.unshift(upload);
    if (this.liveUploadQueue.length > this.maxLiveUploadQueue) {
      this.liveUploadQueue.length = this.maxLiveUploadQueue;
    }
    this.showLiveUpload(upload);
  }

  private showLiveUpload(upload: LiveBuildingUpload): void {
    this.liveUpload = upload;
    this.liveImageIndex = 0;
    this.liveBrokenImages.clear();
    this.startLiveSlider();
  }

  get liveUploadImages(): string[] {
    return (this.liveUpload?.images || []).filter((url) => !this.liveBrokenImages.has(url));
  }

  get liveUploadCode(): string {
    if (this.liveUpload?.buildingCode) {
      return this.liveUpload.buildingCode;
    }
    if (this.liveUpload?.buildingId != null) {
      return `B${this.liveUpload.buildingId}`;
    }
    return '—';
  }

  get liveUploadStaffCountLabel(): string {
    const count = this.liveUpload?.staffCount ?? 0;
    return `${count} staff`;
  }

  get liveUploadTimeLabel(): string {
    if (!this.liveUpload) {
      return '--:--';
    }
    const raw = String(this.liveUpload.uploadedAt || '').trim();
    if (raw) {
      // Already a time-only string (e.g. "10:30 AM" / "10:30:00")
      if (/^\d{1,2}:\d{2}/.test(raw) && !/^\d{4}-\d{2}-\d{2}/.test(raw) && !/^\d{2}-\d{2}-\d{4}/.test(raw)) {
        return raw.replace(/:\d{2}(?=\s|$)/, '').trim();
      }
      const parsed = this.parsePhotoDateTime(raw);
      if (parsed) {
        return this.hhmm(parsed);
      }
    }
    return this.hhmm(new Date(this.liveUpload.receivedAt));
  }

  /** Parse ISO, DD-MM-YYYY HH:mm:ss, or Date-parsable strings. */
  private parsePhotoDateTime(raw: string): Date | null {
    const iso = Date.parse(raw);
    if (!Number.isNaN(iso)) {
      return new Date(iso);
    }
    const m = raw.match(
      /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (m) {
      const [, dd, mm, yyyy, hh = '0', min = '0', ss = '0'] = m;
      const d = new Date(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(min),
        Number(ss)
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  get liveUploadInitials(): string {
    const name = this.liveUpload?.uploadedBy?.trim() || 'S';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  get liveUploadLocationLabel(): string {
    if (!this.liveUpload) {
      return '';
    }
    const place = this.liveUpload.buildingName || this.liveUpload.campusName;
    const state = this.liveUpload.campusName && this.liveUpload.buildingName !== this.liveUpload.campusName
      ? this.liveUpload.campusName
      : this.liveUpload.stateName;
    return state ? `${place} · ${state}` : place;
  }

  prevLiveImage(): void {
    const images = this.liveUploadImages;
    if (!images.length) {
      return;
    }
    this.liveImageIndex = (this.liveImageIndex - 1 + images.length) % images.length;
    this.restartLiveSlider();
  }

  nextLiveImage(auto = false): void {
    const images = this.liveUploadImages;
    if (!images.length) {
      return;
    }

    if (this.liveImageIndex < images.length - 1) {
      this.liveImageIndex += 1;
      if (!auto) {
        this.restartLiveSlider();
      }
      return;
    }

    if (auto && this.liveUploadQueue.length > 1 && this.liveUpload) {
      const currentIdx = this.liveUploadQueue.indexOf(this.liveUpload);
      const nextIdx = currentIdx >= 0 ? (currentIdx + 1) % this.liveUploadQueue.length : 0;
      this.showLiveUpload(this.liveUploadQueue[nextIdx]);
      return;
    }

    this.liveImageIndex = 0;
    if (!auto) {
      this.restartLiveSlider();
    }
  }

  goLiveImage(index: number): void {
    const images = this.liveUploadImages;
    if (!images.length || index < 0 || index >= images.length) {
      return;
    }
    this.liveImageIndex = index;
    this.restartLiveSlider();
  }

  onLiveImageError(url: string): void {
    this.liveBrokenImages.add(url);
    if (!this.liveUploadImages.length) {
      this.liveUpload = null;
      this.stopLiveSlider();
      return;
    }
    if (this.liveImageIndex >= this.liveUploadImages.length) {
      this.liveImageIndex = 0;
    }
  }

  pauseLiveSlider(): void {
    this.liveSliderPaused = true;
    this.stopLiveSlider();
  }

  resumeLiveSlider(): void {
    this.liveSliderPaused = false;
    if (this.liveUpload) {
      this.startLiveSlider();
    }
  }

  private startLiveSlider(): void {
    this.stopLiveSlider();
    if (!this.liveUpload?.images.length || this.liveSliderPaused) {
      return;
    }
    this.liveSliderTimer = setInterval(() => this.nextLiveImage(true), this.liveImageRotateMs);
  }

  private restartLiveSlider(): void {
    if (!this.liveSliderPaused) {
      this.startLiveSlider();
    }
  }

  private stopLiveSlider(): void {
    if (this.liveSliderTimer) {
      clearInterval(this.liveSliderTimer);
      this.liveSliderTimer = null;
    }
  }

  private applyHygieneSummary(summary: HygieneSummaryData): void {
    this.hygieneSummary = summary;

    if (summary.totalBuildings != null) {
      this.total = summary.totalBuildings;
    }
    if (summary.estimatedStaffQty != null) {
      this.totalEmp = summary.estimatedStaffQty;
    }
    if (summary.staffCheckedIn != null) {
      this.empIn = summary.staffCheckedIn;
    }

    const reported =
      summary.buildingsReported ??
      summary.buildingsCleaned ??
      summary.reportedBuildings ??
      summary.campusesCleaned;
    if (reported != null) {
      this.doneCount = reported;
    } else if (summary.hkAttendance != null) {
      this.doneCount = summary.hkAttendance;
    }

    if (this.reportLoopTimer) {
      clearTimeout(this.reportLoopTimer);
      this.reportLoopTimer = null;
    }

    this.progressPct = this.total > 0 ? Math.round((this.doneCount / this.total) * 100) : 0;
    this.lastMilestone = this.total > 0 ? Math.floor((this.doneCount / this.total) * 20) * 5 : 0;
    this.rebuildSparkSeries();
    this.refresh(true);
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.resizeHandler);
    document.addEventListener('fullscreenchange', this.fullscreenHandler);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.initGoogleMap();
        this.ensureMapProjection();
        this.initSeries();
        this.initTickerNews();
        this.boot();
        this.applyTheme();
        this.setSound(true);
        this.clockTimer = setInterval(() => this.tickClock(), 1000);
        this.paceTimer = setInterval(() => this.updatePace(), 15000);
        this.tickClock();
        this.updatePace();
        this.startInchargeAutoScroll();
      });
    });
  }

  pauseInchargeAutoScroll(): void {
    this.inchargeAutoScrollPaused = true;
  }

  resumeInchargeAutoScroll(): void {
    this.inchargeAutoScrollPaused = false;
  }

  private startInchargeAutoScroll(): void {
    this.stopInchargeAutoScroll();
    if (this.reduceMotion) {
      return;
    }

    this.inchargeScrollTimer = setInterval(() => {
      this.tickInchargeAutoScroll();
    }, INCHARGE_AUTO_SCROLL_MS);
  }

  private stopInchargeAutoScroll(): void {
    if (this.inchargeScrollTimer) {
      clearInterval(this.inchargeScrollTimer);
      this.inchargeScrollTimer = null;
    }
  }

  private tickInchargeAutoScroll(): void {
    if (this.inchargeAutoScrollPaused) {
      return;
    }

    const grid = this.inchargeGrid?.nativeElement;
    if (!grid) {
      return;
    }

    const maxScroll = grid.scrollHeight - grid.clientHeight;
    if (maxScroll <= 4) {
      return;
    }

    const step = this.getInchargeScrollStep(grid);
    const nextTop = grid.scrollTop + step;
    if (nextTop >= maxScroll - 2) {
      grid.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    grid.scrollTo({ top: nextTop, behavior: 'smooth' });
  }

  private getInchargeScrollStep(grid: HTMLElement): number {
    const card = grid.querySelector('.icc') as HTMLElement | null;
    if (!card) {
      return 80;
    }

    const style = getComputedStyle(grid);
    const gap = parseFloat(style.rowGap || style.gap || '8') || 8;
    return card.offsetHeight + gap;
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    document.removeEventListener('fullscreenchange', this.fullscreenHandler);
    this.setTvView(false, { skipFullscreen: true });
    this.wsSummaryUnsubscribe?.();
    this.wsSummaryUnsubscribe = null;
    this.wsBuildingImagesUnsubscribe?.();
    this.wsBuildingImagesUnsubscribe = null;
    this.wsBuildingLocationUnsubscribe?.();
    this.wsBuildingLocationUnsubscribe = null;
    this.wsZoneInchargeUnsubscribe?.();
    this.wsZoneInchargeUnsubscribe = null;
    this.wsOnConnectUnsubscribe?.();
    this.wsOnConnectUnsubscribe = null;
    this.locationsSub?.unsubscribe();
    this.locationsSub = null;
    this.staffImagesSub?.unsubscribe();
    this.staffImagesSub = null;
    this.websocket.disconnect();
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.paceTimer) clearInterval(this.paceTimer);
    this.stopInchargeAutoScroll();
    if (this.liveTimer) clearTimeout(this.liveTimer);
    if (this.reportLoopTimer) clearTimeout(this.reportLoopTimer);
    this.stopLiveSlider();
    this.rippleIntervals.forEach((intervalId) => clearInterval(intervalId));
    this.rippleIntervals.clear();
    this.destroyMap3dElement();
    this.resetGoogleMapProjectionState();
    this.mapDomRipples = [];
    this.mapFocusLabel = null;
    if (this.mapFocusRevertTimer) {
      clearTimeout(this.mapFocusRevertTimer);
      this.mapFocusRevertTimer = null;
    }
    this.setSound(false);
    this.clearLayoutSidebarTheme();
  }

  toggleTheme(): void {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    this.applyTheme();
    this.drawAllSparks();
  }

  toggleTvView(): void {
    this.setTvView(!this.tvView);
  }

  private setTvView(on: boolean, options: { skipFullscreen?: boolean } = {}): void {
    this.tvView = on;
    document.body.classList.toggle(this.tvBodyClass, on);

    if (!options.skipFullscreen) {
      if (on) {
        void this.requestFullscreen();
      } else {
        void this.exitFullscreen();
      }
    }

    setTimeout(() => this.refreshMapSize(), 250);
    this.cdr.markForCheck();
  }

  private onFullscreenChange(): void {
    this.ngZone.run(() => {
      if (!document.fullscreenElement && this.tvView) {
        this.setTvView(false, { skipFullscreen: true });
      }
      this.cdr.markForCheck();
    });
  }

  private async requestFullscreen(): Promise<void> {
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) {
      try {
        await el.requestFullscreen();
      } catch {
        // Fullscreen may be blocked; TV layout still applies.
      }
    }
  }

  private async exitFullscreen(): Promise<void> {
    if (document.fullscreenElement && document.exitFullscreen) {
      try {
        await document.exitFullscreen();
      } catch {
        // Ignore if already exited.
      }
    }
  }

  toggleSound(): void {
    this.setSound(!this.soundOn);
  }

  setBaseMode(mode: 'map' | 'sat'): void {
    this.baseMode = mode;
    if (mode === 'map') {
      this.mapViewDrone = false;
      this.stopDroneOrbit();
    }
    this.updateGoogleMapOptions();
    this.cdr.detectChanges();
    void this.syncMap3dMode();
  }

  toggleMap3d(): void {
    if (this.baseMode !== 'sat') {
      return;
    }

    this.mapView3d = !this.mapView3d;
    if (!this.mapView3d) {
      this.mapViewDrone = false;
      this.stopDroneOrbit();
    }
    this.updateGoogleMapOptions();
    this.cdr.detectChanges();
    void this.syncMap3dMode();
  }

  toggleDroneView(): void {
    if (this.baseMode !== 'sat') {
      return;
    }

    const next = !this.mapViewDrone;
    if (next) {
      this.mapView3d = true;
    }

    this.mapViewDrone = next;
    this.updateGoogleMapOptions();
    this.cdr.detectChanges();

    void this.syncMap3dMode().then(() => {
      if (!this.mapViewDrone) {
        this.stopDroneOrbit();
        const camera = this.resolveMap3dCamera();
        this.applyMap3dCamera(camera.position, camera.range, true);
        return;
      }

      const camera = this.resolveMap3dCamera();
      void this.ensureMap3dElement().then(() => {
        this.applyMap3dCamera(camera.position, camera.range, true);
        this.syncDroneOrbit(this.isIndiaMapView());
      });
    });
  }

  onFollowUpViewChange(view: FollowUpView): void {
    this.followUpView = view;
    this.clearIncFilter();
    this.renderIncharges();
  }

  onInchargeClick(idx: number): void {
    if (this.selectedIncharge === idx) {
      this.clearIncFilter();
    } else {
      this.selectedIncharge = idx;
      this.filterInchargeName = this.inchargeCards.find((c) => c.i === idx)?.n || '';
    }
    this.renderIncharges();
  }

  clearIncFilter(): void {
    this.selectedIncharge = null;
    this.filterInchargeName = '';
  }

  private applyTheme(): void {
    const stage = this.host.nativeElement.querySelector('.hkdb-stage') as HTMLElement | null;
    if (stage) stage.setAttribute('data-theme', this.theme);
    this.syncLayoutSidebarTheme();
    this.updateGoogleMapOptions();
  }

  private syncLayoutSidebarTheme(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.setAttribute('data-hkdb-theme', this.theme);
  }

  private clearLayoutSidebarTheme(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.removeAttribute('data-hkdb-theme');
  }

  private renderIncharges(): void {
    const groups = new Map<string, { name: string; done: number; total: number }>();
    this.zoneInchargeRows.forEach((row, index) => {
      const name = this.followUpLabel(row, index);
      const key = name.toLowerCase();
      const prev = groups.get(key) ?? { name, done: 0, total: 0 };
      prev.done += this.toNonNegNumber(row.photoTakenBuildings);
      prev.total += this.toNonNegNumber(row.totalBuildings);
      groups.set(key, prev);
    });

    this.inchargeCards = Array.from(groups.values())
      .map((group, i) => {
        const pct = group.total > 0 ? Math.round((group.done / group.total) * 100) : 0;
        return {
          n: group.name,
          i,
          done: group.done,
          total: group.total,
          pct: Math.max(0, Math.min(100, pct)),
          low: group.total > 0 ? group.done / group.total < 0.5 : pct < 50,
        };
      })
      .sort((a, b) => b.pct - a.pct || b.total - a.total || b.done - a.done);

    if (
      this.selectedIncharge != null &&
      !this.inchargeCards.some((card) => card.i === this.selectedIncharge)
    ) {
      this.clearIncFilter();
    }
  }

  private readonly sparkLen = 24;

  private initSeries(): void {
    this.rebuildSparkSeries();
  }

  private rebuildSparkSeries(): void {
    const pend = Math.max(0, this.total - this.doneCount);
    this.cleanSeries = [];
    this.pendSeries = [];
    this.inSeries = [];
    for (let i = 0; i < this.sparkLen; i++) {
      const f = i / (this.sparkLen - 1);
      this.cleanSeries.push(Math.round(this.doneCount * (0.25 + 0.75 * f)));
      this.pendSeries.push(Math.round(pend + this.doneCount * 0.75 * (1 - f)));
      this.inSeries.push(Math.round(this.empIn * (0.25 + 0.75 * f)));
    }
  }

  private drawSpark(id: string, arr: number[], color: string): void {
    const el = this.$(id);
    if (!el || arr.length < 2) return;
    const max = Math.max(...arr);
    const min = Math.min(...arr);
    const rng = max - min;
    let pts: string;
    let lastY: number;

    if (rng === 0) {
      const upward = id !== 'sparkPend';
      pts = arr
        .map((_, i) => {
          const x = (i / (arr.length - 1)) * 62;
          const t = i / (arr.length - 1);
          const y = upward ? 22 - t * 14 : 8 + t * 14;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');
      lastY = upward ? 8 : 22;
    } else {
      pts = arr
        .map((v, i) => {
          const x = (i / (arr.length - 1)) * 62;
          const y = 24 - ((v - min) / rng) * 20 - 2;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');
      lastY = 24 - ((arr[arr.length - 1] - min) / rng) * 20 - 2;
    }

    el.innerHTML = `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round" opacity=".9"/><circle cx="62" cy="${lastY.toFixed(1)}" r="1.8" fill="${color}"/>`;
  }

  private cssVar(name: string): string {
    const stage = this.host.nativeElement.querySelector('.hkdb-stage') as HTMLElement | null;
    return stage
      ? getComputedStyle(stage).getPropertyValue(name).trim()
      : getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  private drawAllSparks(): void {
    this.drawSpark('sparkClean', this.cleanSeries, this.cssVar('--ok'));
    this.drawSpark('sparkPend', this.pendSeries, this.cssVar('--alert'));
    this.drawSpark('sparkIn', this.inSeries, this.cssVar('--info'));
  }

  private updatePace(): void {
    const t = this.istNow();
    const mins = t.getHours() * 60 + t.getMinutes();
    const exp = Math.max(0, Math.min(1, (mins - 480) / (1080 - 480)));
    const act = this.total > 0 ? this.doneCount / this.total : 0;
    this.progressPct = act * 100;
    this.expectedPct = exp * 100;
    this.expectedLabel = `EXPECTED ${Math.round(exp * 100)}%`;

    const diff = act - exp;
    let label: string;
    let colorVar: string;
    if (exp <= 0.001) {
      label = 'Shift starting';
      colorVar = '--info';
    } else if (diff >= 0.05) {
      label = '▲ Ahead of plan';
      colorVar = '--ok';
    } else if (diff <= -0.05) {
      label = `▼ Behind ${Math.round(-diff * 100)}%`;
      colorVar = '--alert';
    } else {
      label = '● On track';
      colorVar = '--brand';
    }
    this.paceStateLabel = label;
    const color = this.cssVar(colorVar);
    this.paceStateColor = color;
    this.paceStateBg = `color-mix(in srgb, ${color} 16%, transparent)`;
  }

  private initTickerNews(): void {
    const m = this.$('tickMove');
    if (!m) return;
    NEWS.forEach((n) => {
      const s = document.createElement('span');
      s.className = 'e';
      s.innerHTML = `<b>UPDATE</b> · ${n}`;
      m.appendChild(s);
    });
  }

  private animateNum(el: HTMLElement, to: number, dur = 850): void {
    const from = parseInt(el.textContent?.replace(/[^0-9]/g, '') ?? '0', 10) || 0;
    if (from === to) {
      el.textContent = this.fmt(to);
      return;
    }
    const start = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      el.textContent = this.fmt(from + (to - from) * e);
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  private pop(id: string): void {
    const el = this.$(id);
    if (!el) return;
    el.classList.remove('show');
    void el.offsetWidth;
    el.classList.add('show');
  }

  private setNumbers(anim: boolean): void {
    const pend = this.total - this.doneCount;
    const set = (id: string, v: number) => {
      const el = this.$(id);
      if (!el) return;
      if (anim) this.animateNum(el, v);
      else el.textContent = this.fmt(v);
    };
    set('sClean', this.doneCount);
    set('sIn', this.empIn);
    set('sPend', pend);
    this.updateStaffPendingStat();
    this.setText('sTotal', this.fmt(this.total));
    this.setText('sEmp', this.fmt(this.totalEmp));
  }

  private updateStaffPendingStat(): void {
    const raw = this.totalEmp - this.empIn;
    if (raw < 0) {
      this.staffPendingLabel = 'Extra Staff';
      this.staffPendingSub = 'above shift allocation';
      this.staffPendingDisplay = `+${this.fmt(Math.abs(raw))}`;
      return;
    }

    this.staffPendingLabel = 'Staff Pending';
    this.staffPendingSub = 'not yet logged in';
    this.staffPendingDisplay = this.fmt(raw);
  }

  private setPeripherals(): void {
    const pend = this.total - this.doneCount;
    const pct = this.total > 0 ? Math.round((this.doneCount / this.total) * 100) : 0;
    this.setText('sCleanPct', `${pct}%`);
    this.setText('sPendPct', `${this.total > 0 ? 100 - pct : 0}%`);
    this.setText('sInPct', `${this.totalEmp > 0 ? Math.round((this.empIn / this.totalEmp) * 100) : 0}%`);
    this.setText('progPct', String(pct));
    this.updateMapLegendFromLocations();
    this.renderIncharges();
    const cutoff = Date.now() - 3600000;
    this.hourStamps = this.hourStamps.filter((stamp) => stamp > cutoff);
    this.updatePace();
    this.drawAllSparks();
  }

  private refresh(anim: boolean): void {
    this.setNumbers(anim);
    this.setPeripherals();
  }

  private setSound(on: boolean): void {
    this.soundOn = on;
    if (on) {
      this.initAudio();
      this.audioCtx?.resume();
      if (this.masterGain && this.audioCtx) {
        this.masterGain.gain.setTargetAtTime(0.85, this.audioCtx.currentTime, 0.15);
      }
    } else if (this.audioCtx && this.masterGain) {
      this.masterGain.gain.setTargetAtTime(0, this.audioCtx.currentTime, 0.15);
    }
  }

  private initAudio(): void {
    if (this.audioCtx) return;
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.audioCtx = new AC();
    this.masterGain = this.audioCtx.createGain();
    this.masterGain.gain.value = 0;
    this.masterGain.connect(this.audioCtx.destination);
  }

  private blip(idx = 0): void {
    if (!this.soundOn || !this.audioCtx || !this.masterGain) {
      return;
    }

    const f = PENTA[Math.max(0, idx) % PENTA.length];
    const t = this.audioCtx.currentTime;
    const o = this.audioCtx.createOscillator();
    o.type = 'triangle';
    const g = this.audioCtx.createGain();
    o.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.07, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(g);
    g.connect(this.masterGain);
    o.start(t);
    o.stop(t + 0.24);
  }

  private checkMilestone(): void {
    if (this.total <= 0) {
      return;
    }

    const milestone = Math.floor((this.doneCount / this.total) * 20) * 5;
    if (milestone <= this.lastMilestone) {
      return;
    }

    this.lastMilestone = milestone;
    this.chime();
    const el = this.$('progPct');
    if (!el) {
      return;
    }
    el.classList.remove('ping');
    void el.offsetWidth;
    el.classList.add('ping');
  }

  private chime(): void {
    if (!this.soundOn || !this.audioCtx || !this.masterGain) return;
    const t0 = this.audioCtx.currentTime;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
      const t = t0 + i * 0.085;
      const o = this.audioCtx!.createOscillator();
      o.type = 'sine';
      const g = this.audioCtx!.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.13, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
      o.connect(g);
      g.connect(this.masterGain!);
      o.start(t);
      o.stop(t + 0.72);
    });
  }

  private boot(): void {
    ['sTotal', 'sEmp', 'sClean', 'sIn', 'sPend'].forEach((id) => this.setText(id, '0'));
    this.staffPendingDisplay = '0';
    this.setPeripherals();
    const bootVals: [string, number][] = [
      ['sTotal', this.total],
      ['sEmp', this.totalEmp],
      ['sClean', this.doneCount],
      ['sIn', this.empIn],
      ['sPend', this.total - this.doneCount],
    ];
    bootVals.forEach(([id, val], i) => {
      setTimeout(() => {
        const el = this.$(id);
        if (el) this.animateNum(el, val, 1000);
        if (i === bootVals.length - 1) {
          setTimeout(() => this.updateStaffPendingStat(), 1000);
        }
      }, 110 * i);
    });
  }

  private pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  private istNow(): Date {
    return new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000);
  }

  private hhmm(d: Date): string {
    return `${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
  }

  private tickClock(): void {
    const t = this.istNow();
    this.clockLabel = `${this.pad(t.getHours())}:${this.pad(t.getMinutes())}:${this.pad(t.getSeconds())}`;
    this.dateLabel = t
      .toLocaleDateString('en-IN', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
      .toUpperCase();

    // Full page refresh at IST midnight (12:00 AM) for the new day
    const dateKey = this.istDateKey(t);
    if (!this.lastIstDateKey) {
      this.lastIstDateKey = dateKey;
      return;
    }
    if (dateKey !== this.lastIstDateKey) {
      console.log(
        '[HousekeepingDashboard] IST day changed',
        this.lastIstDateKey,
        '→',
        dateKey,
        '— reloading page'
      );
      this.lastIstDateKey = dateKey;
      window.location.reload();
    }
  }

  private $(id: string): HTMLElement | null {
    return this.host.nativeElement.querySelector(`#${id}`);
  }

  private setText(id: string, value: string): void {
    const el = this.$(id);
    if (el) el.textContent = value;
  }

  private fmt(n: number): string {
    return Math.round(n).toLocaleString('en-IN');
  }
}
